import { verifySession } from '../utils/session';
import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { useUser } from './UserContext';
import axios from 'axios';
import useTabCountdown from '../hooks/useTabCountdown';
import { handleTokenInvalidation } from '../utils/tokenHandler';

// 创建WebSocket上下文
const WebSocketContext = createContext();

// WebSocket消息类型常量
export const MESSAGE_TYPES = {
  INIT: 0,              // 初始化
  TIME_CHECK: 1,        // 校验时间
  USER_COUNT_CHANGE: 2, // 人数变化
  FIGHT_INFO_CHANGE: 3, // 对战信息变化
  PAUSE: 4,             // 暂停
  RESUME: 5,            // 继续
  FIGHT_START: 10,      // 比赛开始（进入作图阶段）
  UPLOAD_STAGE: 20,     // 进入上传阶段
  CREATION_UPLOAD: 21,  // 上传作品变化（admin）
  VOTE_STAGE: 30,       // 进入投票阶段
  VOTE_CHANGE: 31,      // 投票变化
  FIGHT_END: 40         // 比赛结束
};

// 对战状态常量
export const FIGHT_STATUS = {
  NO_FIGHT: 0,       // 无对战
  CREATION_STAGE: 10, // 创作阶段
  UPLOAD_STAGE: 20,   // 上传阶段
  VOTE_STAGE: 30,     // 投票阶段
  END_STAGE: 40       // 结束阶段
};

// WebSocket URL 配置
const WS_BASE_URL = window.location.protocol === 'https:' ? 'wss://server.battleverse.cn' : 'ws://server.battleverse.cn';
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_DELAY = 3000;

// 在组件顶部添加移动端检测
const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

export const WebSocketProvider = ({ children }) => {
  const { getToken } = useUser();
  const [isConnected, setIsConnected] = useState(false);
  const [fightInfo, setFightInfo] = useState(null);
  const [creationList, setCreationList] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [fightStatus, setFightStatus] = useState(0);
  const [isStop, setIsStop] = useState(0);
  const [lastSecond, setLastSecond] = useState(0);
  const [voteMax, setVoteMax] = useState(0);
  const [endSecond, setEndSecond] = useState(0);
  const [newCreation, setNewCreation] = useState(null);
  const [newVote, setNewVote] = useState(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const reconnectAttemptRef = useRef(0);
  const [fightEndId, setFightEndId] = useState(null);
  const [formData, setFormData] = useState({
    fightName: '',
    fightDesc: '',
    plotTime: '480',
    uploadTime: '120',
    voteTime: '120',
    uploadPicCount: '1',
    realNameType: '1',
    voteConfType: '1',
    voteConf1Num: '1',
    voteConf2Num: '5'
  });

  useEffect(() => {
    window.__PB_LIVE_BATTLE__ = fightStatus > 0 && fightStatus < 40;
    return () => { window.__PB_LIVE_BATTLE__ = false; };
  }, [fightStatus]);

  const wsRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastServerSyncRef = useRef(Date.now());
  const heartbeatInterval = 10000;
  const reconnectTimeoutRef = useRef(null);
  const prevFightStatusRef = useRef(0);

  // 添加音频上下文的ref
  const audioContextRef = useRef(null);

  // 添加标签页倒计时功能
  const getCountdownPrefix = () => {
    switch (fightStatus) {
      case FIGHT_STATUS.CREATION_STAGE:
        return '创作阶段 ';
      case FIGHT_STATUS.UPLOAD_STAGE:
        return '上传阶段 ';
      case FIGHT_STATUS.VOTE_STAGE:
        return '投票阶段 ';
      default:
        return '';
    }
  };

  // 使用标签页倒计时hook
  const { resetTimer } = useTabCountdown(
    lastSecond,
    getCountdownPrefix(),
    ' - PromptBattle',
    // 只在有对战且不处于结束阶段时启用倒计时
    fightStatus > 0 && fightStatus < FIGHT_STATUS.END_STAGE && isStop === 0,
    fightInfo?.fightNameCn ? `${fightInfo.fightNameCn} - PromptBattle` : 'PromptBattle'
  );

  // 当服务器发送新的倒计时时间时重置倒计时
  useEffect(() => {
    if (lastSecond > 0 && fightStatus > 0 && fightStatus < FIGHT_STATUS.END_STAGE && isStop === 0) {
      resetTimer(lastSecond);
    }
  }, [lastSecond, fightStatus, isStop, resetTimer]);

  // 清理函数
  const connectionGeneration = useRef(0);
  const cleanup = useCallback(() => {
    connectionGeneration.current++;
    try {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        const ws = wsRef.current;
        ws.onopen = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close();
        }
      }
      wsRef.current = null;
      setIsConnected(false);
    } catch (error) {
      console.error('清理WebSocket资源时出错:', error);
    }
  }, []);

  // 发送心跳
  const sendHeartbeat = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 1 }));
    }
  }, []);

  // 启动心跳检测
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, heartbeatInterval);
  }, [sendHeartbeat]);

  // 启动本地倒计时
  const startCountdown = useCallback(() => {
    // 如果已经在运行，不要重新启动
    if (countdownIntervalRef.current) {
      console.log('倒计时已在运行中，跳过启动');
      return;
    }

    // 只有在非暂停状态且有剩余时间时才启动倒计时
    if (isStop === 0 && lastSecond > 0) {
      console.log('启动本地倒计时，初始时间:', lastSecond);

      // 记录启动倒计时的时间
      lastServerSyncRef.current = Date.now();
      let startTime = Date.now();
      let previousTick = startTime;
      let accumulatedError = 0;

      // 使用requestAnimationFrame来提高精度
      const tick = () => {
        const now = Date.now();
        const deltaTime = now - previousTick;
        previousTick = now;

        // 累积误差
        accumulatedError += deltaTime;

        // 当累积误差达到或超过1秒时更新
        if (accumulatedError >= 1000) {
          const secondsToSubtract = Math.floor(accumulatedError / 1000);
          accumulatedError %= 1000;

          setLastSecond(prevTime => {
            const currentTime = typeof prevTime === 'number' ? Math.floor(prevTime) : parseInt(prevTime, 10) || 0;

            // 如果已暂停或时间已到，停止倒计时
            if (isStop === 1 || currentTime <= 0) {
              if (countdownIntervalRef.current) {
                cancelAnimationFrame(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              }
              return currentTime <= 0 ? 0 : currentTime;
            }

            // 计算新的时间，确保不会小于0
            return Math.max(0, currentTime - secondsToSubtract);
          });
        }

        // 如果倒计时仍在运行，继续下一帧
        if (countdownIntervalRef.current) {
          countdownIntervalRef.current = requestAnimationFrame(tick);
        }
      };

      // 启动动画帧循环
      countdownIntervalRef.current = requestAnimationFrame(tick);
    }
  }, [isStop, lastSecond]);

  // 处理服务器时间同步
  const syncTimeWithServer = useCallback((serverTime) => {
    if (serverTime === undefined) return;

    console.log('同步服务器时间:', serverTime);

    // 确保转换为整数
    let validTime;
    try {
      // 先转换为数字，然后确保是整数
      validTime = typeof serverTime === 'number' ? Math.floor(serverTime) : parseInt(serverTime, 10);
    } catch (e) {
      console.error('解析服务器时间失败:', e);
      validTime = 0;
    }

    // 确保时间有效
    validTime = isNaN(validTime) ? 0 : Math.max(0, validTime);

    // 更新本地时间
    setLastSecond(validTime);

    // 记录同步时间点
    lastServerSyncRef.current = Date.now();

    // 只有在时间差异较大时才重启倒计时
    if (Math.abs(validTime - lastSecond) > 2) {
      // 先停止现有的倒计时
      if (countdownIntervalRef.current) {
        cancelAnimationFrame(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      // 然后重新启动
      startCountdown();
    }
  }, [lastSecond, startCountdown]);

  // 发送消息
  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        const messageStr = JSON.stringify(message);
        wsRef.current.send(messageStr);
        return true;
      } catch (error) {
        console.error('发送消息失败:', error);
        return false;
      }
    } else {
      console.error('WebSocket 未连接，无法发送消息');
      return false;
    }
  }, []);

  // 修改 playTimerSound 函数
  const playTimerSound = useCallback(() => {
    console.log('尝试播放计时器音效');

    try {
      if (isMobile) {
        // 移动端使用 Web Audio API
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
          // 每次播放都创建新的音频上下文
          const audioContext = new AudioContext();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          // 设置音量
          gainNode.gain.value = 0.5;

          // 连接节点
          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          // 设置频率和类型
          oscillator.type = 'sine';
          oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // 使用更高的频率

          // 设置音量渐变
          gainNode.gain.setValueAtTime(0, audioContext.currentTime);
          gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.01);
          gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);

          // 播放音频
          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.3);

          // 播放结束后关闭上下文
          setTimeout(() => {
            audioContext.close();
          }, 500);
        }
      } else {
        // PC端使用普通音频元素
        const audioElement = document.getElementById('timer-sound');
        if (audioElement) {
          audioElement.currentTime = 0;
          audioElement.volume = 1.0;
          audioElement.play().catch(error => {
            console.warn('PC端音频播放失败:', error);
          });
        }
      }
    } catch (error) {
      console.error('播放音频时出错:', error);
    }
  }, []);

  // 添加音频初始化
  useEffect(() => {
    if (!isMobile) {
      // 只在PC端初始化音频元素
      let audioElement = document.getElementById('timer-sound');
      if (!audioElement) {
        audioElement = document.createElement('audio');
        audioElement.id = 'timer-sound';
        audioElement.src = '/timer.mp3';
        audioElement.preload = 'auto';
        document.body.appendChild(audioElement);
      }
    }

    return () => {
      // 清理
      const audioElement = document.getElementById('timer-sound');
      if (audioElement && audioElement.parentNode) {
        audioElement.parentNode.removeChild(audioElement);
      }
      // 清理音频上下文
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
    };
  }, []);

  // 添加本地存储相关的函数
  const saveFightInfoToLocal = useCallback((info) => {
    if (info) {
      localStorage.setItem('currentFightInfo', JSON.stringify(info));
    }
  }, []);

  const loadFightInfoFromLocal = useCallback(() => {
    try {
      const savedInfo = localStorage.getItem('currentFightInfo');
      if (savedInfo) {
        return JSON.parse(savedInfo);
      }
    } catch (error) {
      console.error('Failed to load fight info from local storage:', error);
    }
    return null;
  }, []);

  // 在组件初始化时尝试从本地存储恢复对战信息
  useEffect(() => {
    try {
      const savedInfo = localStorage.getItem('currentFightInfo');
      if (savedInfo) {
        const parsedInfo = JSON.parse(savedInfo);
        // 只在非结束状态时恢复信息
        if (parsedInfo.status && parsedInfo.status !== FIGHT_STATUS.END_STAGE) {
          setFightInfo(parsedInfo);
          setFightStatus(parsedInfo.status);
        }
      }
    } catch (error) {
      console.error('从本地存储恢复对战信息失败:', error);
    }
  }, []);

  // 当对战信息更新时保存到本地存储
  useEffect(() => {
    if (fightInfo) {
      saveFightInfoToLocal({
        ...fightInfo,
        status: fightStatus
      });
    }
  }, [fightInfo, fightStatus, saveFightInfoToLocal]);

  // 在对战结束时清除本地存储和状态
  useEffect(() => {
    if (fightStatus === FIGHT_STATUS.NO_FIGHT) {
      // 清除本地存储的对战信息
      localStorage.removeItem('currentFightInfo');
      // 清除所有状态
      setFightInfo(null);
      setCreationList([]);
      setLastSecond(0);
      setVoteMax(0);
      setIsStop(0);
      setUserCount(0);
      setNewCreation(null);
      setNewVote(null);
      setFormData({
        fightName: '',
        fightDesc: '',
        plotTime: '480',
        uploadTime: '120',
        voteTime: '120',
        uploadPicCount: '1',
        realNameType: '1',
        voteConfType: '1',
        voteConf1Num: '1',
        voteConf2Num: '5'
      });
    }
    // 在END_STAGE状态下不再重置状态，只保存当前的fightInfo到本地以便查看结果
    else if (fightStatus === FIGHT_STATUS.END_STAGE && fightInfo) {
      localStorage.setItem('endedFightInfo', JSON.stringify({
        id: fightInfo.id,
        fightName: fightInfo.fightName,
        fightDesc: fightInfo.fightDesc,
        status: fightStatus
      }));
    }
  }, [fightStatus, fightInfo]);

  // 添加 handleMessage 函数定义
  const handleMessage = useCallback((event) => {
    try {
      const messageData = JSON.parse(event.data);

      // 检查是否有token无效的错误
      if (messageData.code === -2) {
        console.error('WebSocket收到token无效的响应:', messageData);
        handleTokenInvalidation(messageData.msg || '登录已失效，请重新登录');
        cleanup();
        return;
      }

      switch (messageData.type) {
        case MESSAGE_TYPES.INIT:
          if (messageData.data) {
            if (messageData.data.creationList) setCreationList(messageData.data.creationList);
            if (messageData.data.fightingDTO) {
              const processedFightInfo = {
                ...messageData.data.fightingDTO,
                fightNameCn: messageData.data.fightingDTO.fightNameCn || messageData.data.fightingDTO.fightName?.split('|')[0]?.trim() || '',
                fightNameEn: messageData.data.fightingDTO.fightNameEn || messageData.data.fightingDTO.fightName?.split('|')[1]?.trim() || '',
                fightDesc: messageData.data.fightingDTO.fightDesc || ''
              };
              setFightInfo(processedFightInfo);
              // 保存到本地存储
              localStorage.setItem('currentFightInfo', JSON.stringify({
                ...processedFightInfo,
                status: messageData.data.fightStatus
              }));
            }
            if (messageData.data.fightStatus !== undefined) setFightStatus(messageData.data.fightStatus);
            if (messageData.data.isStop !== undefined) setIsStop(messageData.data.isStop);
            if (messageData.data.lastSecond !== undefined) syncTimeWithServer(messageData.data.lastSecond);
            if (messageData.data.voteMax !== undefined) setVoteMax(messageData.data.voteMax);
          }
          break;

        case MESSAGE_TYPES.TIME_CHECK:
          if (messageData.data) {
            if (messageData.data.fightStatus !== undefined) setFightStatus(messageData.data.fightStatus);
            if (messageData.data.isStop !== undefined) setIsStop(messageData.data.isStop);
            if (messageData.data.lastSecond !== undefined) syncTimeWithServer(messageData.data.lastSecond);
          }
          break;

        case MESSAGE_TYPES.USER_COUNT_CHANGE:
          if (messageData.data?.userCount !== undefined) {
            setUserCount(messageData.data.userCount);
          }
          break;

        case MESSAGE_TYPES.FIGHT_INFO_CHANGE:
          if (messageData.data?.fightingDTO) {
            const updatedFightInfo = {
              ...messageData.data.fightingDTO,
              fightNameCn: messageData.data.fightingDTO.fightNameCn || messageData.data.fightingDTO.fightName?.split('|')[0]?.trim() || '',
              fightNameEn: messageData.data.fightingDTO.fightNameEn || messageData.data.fightingDTO.fightName?.split('|')[1]?.trim() || '',
              fightDesc: messageData.data.fightingDTO.fightDesc || ''
            };
            setFightInfo(updatedFightInfo);
            // 保存更新后的信息到本地存储
            localStorage.setItem('currentFightInfo', JSON.stringify({
              ...updatedFightInfo,
              status: fightStatus
            }));
          }
          break;

        case MESSAGE_TYPES.FIGHT_START:
        case MESSAGE_TYPES.UPLOAD_STAGE:
        case MESSAGE_TYPES.VOTE_STAGE:
          if (messageData.data) {
            if (messageData.data.fightingDTO) {
              const processedFightInfo = {
                ...messageData.data.fightingDTO,
                theme: messageData.data.fightingDTO.theme || messageData.data.fightingDTO.fightName || '',
                fightName: messageData.data.fightingDTO.fightName || ''
              };
              setFightInfo(processedFightInfo);
              saveFightInfoToLocal({
                ...processedFightInfo,
                status: messageData.data.fightStatus
              });
            }
            if (messageData.data.fightStatus !== undefined) {
              const newStatus = messageData.data.fightStatus;
              const oldStatus = fightStatus;
              setFightStatus(newStatus);

              // 在阶段变化时播放铃声
              if (oldStatus !== newStatus && isStop === 0) {
                playTimerSound();
              }
            }
            if (messageData.data.isStop !== undefined) setIsStop(messageData.data.isStop);
            if (messageData.data.lastSecond !== undefined) syncTimeWithServer(messageData.data.lastSecond);
          }
          break;

        case MESSAGE_TYPES.CREATION_UPLOAD:
          if (messageData.data) {
            setNewCreation(messageData.data.creation || messageData.data);
            window.dispatchEvent(new Event('pb:creations-changed'));
          }
          break;

        case MESSAGE_TYPES.VOTE_CHANGE:
          if (messageData.data) {
            setNewVote(messageData.data);
            window.dispatchEvent(new Event('pb:creations-changed'));
          }
          break;

        case MESSAGE_TYPES.FIGHT_END:
          if (messageData.data) {
            if (messageData.data.fightStatus !== undefined) {
              const newStatus = messageData.data.fightStatus;
              const oldStatus = fightStatus;
              setFightStatus(newStatus);

              // 在进入结束阶段时播放铃声
              if (oldStatus !== newStatus && oldStatus !== FIGHT_STATUS.NO_FIGHT) {
                playTimerSound();
              }
            }
            window.dispatchEvent(new Event('pb:history-changed'));
            const endedId = messageData.data.fightId || messageData.data.fightingDTO?.fightId;
            // 保存结束对战ID
            if (endedId !== undefined) {
              setFightEndId(endedId);

              // 保存结束对战的ID，并跳转到结果页面
              // 使用window.location进行导航，确保页面重新加载
              localStorage.setItem('endedFightId', endedId);

              // 延迟以允许铃声播放完成
              setTimeout(() => {
                if (/^\/battle(?:-settings)?\/?$/.test(window.location.pathname)) {
                  window.location.href = `/battle-result/${endedId}`;
                }
              }, 2000);
            }

            // 设置isStop为0表示对战已经结束
            setIsStop(0);
          }
          break;
      }
    } catch (error) {
      console.error('处理WebSocket消息时出错:', error);
    }
  }, [fightStatus, syncTimeWithServer]);

  // 修改重连逻辑
  const connect = useCallback(async () => {
    const generation = connectionGeneration.current;
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        console.error('No token available for WebSocket connection');
        return;
      }

      if (!await verifySession(token)) return;
      if (generation !== connectionGeneration.current || getToken() !== token) return;
      if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) return;
      const ws = new WebSocket(WS_BASE_URL + '/ws.do?token=' + token);
      wsRef.current = ws;

      ws.onopen = () => {
        if (wsRef.current !== ws) { ws.close(); return; }
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttemptRef.current = 0;
        setReconnectAttempts(0);

        // 连接成功后立即发送初始化消息
        sendMessage({ type: 0 });

        // 启动心跳检测
        startHeartbeat();
      };

      ws.onclose = (event) => {
        if (wsRef.current !== ws) return;
        console.log('WebSocket disconnected', event);
        setIsConnected(false);
        cleanup();

        // 检查是否是因为token无效导致的关闭
        // WebSocket关闭代码1000表示正常关闭，1006表示异常关闭
        if (event.code === 1000 && event.reason && (
            event.reason.includes('token') ||
            event.reason.includes('登录') ||
            event.reason.includes('认证')
        )) {
          // 可能是token无效导致的关闭
          handleTokenInvalidation('WebSocket连接断开，请重新登录');
          return;
        }

        // Keep logged-in spectators connected too, so they receive the next round.
        if (getToken()) {
          const attempt = reconnectAttemptRef.current++;
          const timeout = Math.min(1000 * Math.pow(2, attempt), 30000) + Math.random() * 500;
          reconnectTimeoutRef.current = setTimeout(() => {
            setReconnectAttempts(prev => prev + 1);
            connect();
          }, timeout);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        // A failed connection emits close next; keep that handler so it can retry.
        if (wsRef.current === ws) setIsConnected(false);
      };

      // 使用修改后的消息处理逻辑
      ws.onmessage = handleMessage;
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      setIsConnected(false);
    }
  }, [getToken, cleanup, startHeartbeat, sendMessage, reconnectAttempts, handleMessage]);

  // 清除新创作通知
  const clearNewCreation = useCallback(() => {
    setNewCreation(null);
  }, []);

  // 清除新投票通知
  const clearNewVote = useCallback(() => {
    setNewVote(null);
  }, []);

  // 监听 token 变化，重新连接
  useEffect(() => {
    let mounted = true;

    const initializeWebSocket = async () => {
      const token = getToken();
      if (token && mounted) {
        console.log('Token变化，准备建立WebSocket连接');
        await connect();
      }
    };

    initializeWebSocket();

    const resumeOnline = () => {
      if (!getToken()) return;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectAttemptRef.current = 0;
      connect();
    };
    window.addEventListener('online', resumeOnline);
    window.addEventListener('pb-session-invalid', cleanup);

    return () => {
      mounted = false;
      window.removeEventListener('online', resumeOnline);
      window.removeEventListener('pb-session-invalid', cleanup);
      reconnectAttemptRef.current = 0;
      setReconnectAttempts(0); // 重置重连次数
      cleanup();
    };
  }, [getToken]); // 移除 connect 和 cleanup 依赖，避免循环依赖

  // 监听倒计时状态变化
  useEffect(() => {
    // 当isStop或lastSecond变化时，可能需要启动或停止倒计时
    if (isStop === 0 && lastSecond > 0) {
      // 只有在计时器未运行时才启动
      if (!countdownIntervalRef.current) {
        startCountdown();
      }
    } else if (isStop === 1 || lastSecond <= 0) {
      // 暂停或时间到，停止倒计时
      if (countdownIntervalRef.current) {
        cancelAnimationFrame(countdownIntervalRef.current);
        countdownIntervalRef.current = null;

        // 如果是因为时间到而停止，且不是暂停状态，播放铃声
        if (lastSecond <= 0 && isStop === 0 && fightStatus > 0 && fightStatus < FIGHT_STATUS.END_STAGE) {
          playTimerSound();
        }
      }
    }

    // 组件卸载时清理
    return () => {
      if (countdownIntervalRef.current) {
        cancelAnimationFrame(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [isStop, lastSecond, startCountdown, fightStatus, playTimerSound]);

  // 添加监听阶段变化的effect
  useEffect(() => {
    const prevStatus = prevFightStatusRef.current;

    // 如果状态发生变化，且不是因为暂停，播放铃声
    if (prevStatus !== fightStatus && isStop === 0 && fightStatus > 0) {
      // 在所有阶段变化时播放铃声，包括：
      // 1. 从创作阶段到上传阶段
      // 2. 从上传阶段到投票阶段
      // 3. 从投票阶段到结束阶段
      // 4. 手动结束当前阶段
      // 5. 结束整轮对战
      if (prevStatus !== FIGHT_STATUS.NO_FIGHT && (
        fightStatus === FIGHT_STATUS.UPLOAD_STAGE ||
        fightStatus === FIGHT_STATUS.VOTE_STAGE ||
        fightStatus === FIGHT_STATUS.END_STAGE
      )) {
        playTimerSound();
      }
    }

    // 更新前一个状态
    prevFightStatusRef.current = fightStatus;
  }, [fightStatus, isStop, playTimerSound]);

  // 获取作品列表
  const fetchCreations = useCallback(async () => {
    try {
      const token = getToken();
      const response = await axios.post('//server.battleverse.cn/fight/creations.do',
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      if (response.data.success) {
        setCreationList(response.data.data);
      } else if (response.data.code === -2) {
        // 处理token无效的情况
        handleTokenInvalidation(response.data.msg || '登录已失效，请重新登录');
      }
    } catch (error) {
      console.error('获取作品列表失败:', error);
    }
  }, [getToken]);

  // 格式化倒计时时间为时:分:秒格式
  const formatCountdown = useCallback((seconds) => {
    if (seconds <= 0) return '00:00'; // 如果时间小于等于0，直接返回00:00

    // 确保seconds是整数
    seconds = Math.floor(seconds);

    const hours = Math.floor(seconds / 3600); // 计算小时数
    const minutes = Math.floor((seconds % 3600) / 60); // 计算分钟数
    const remainingSeconds = seconds % 60; // 计算剩余的秒数

    // 如果小时为0，则返回分:秒格式（包括当分钟为0的情况）
    if (hours === 0) {
      return `${minutes < 10 ? '0' + minutes : minutes}:${remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds}`;
    }

    // 否则返回完整的时:分:秒格式
    return `${hours}:${minutes < 10 ? '0' + minutes : minutes}:${remainingSeconds < 10 ? '0' + remainingSeconds : remainingSeconds}`;
  }, []);

  const contextValue = {
    isConnected,
    fightInfo,
    creationList,
    userCount,
    fightStatus,
    isStop,
    lastSecond,
    voteMax,
    endSecond,
    newCreation,
    newVote,
    fightEndId,
    formData,
    setFormData,
    fetchCreations,
    sendHeartbeat,
    connect,
    cleanup,
    startHeartbeat,
    startCountdown,
    formatCountdown,
    setFightInfo,
    sendMessage,
    clearNewCreation,
    clearNewVote,
    FIGHT_STATUS,
    playTimerSound
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// 自定义Hook，用于在组件中使用WebSocket上下文
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket必须在WebSocketProvider内部使用');
  }
  return context;
};
